import { FilterState } from "@/components/Page/ConsistencyChecks/Filters/FilterForm";
import { useCurrentOrganization } from "@/modules/Organizations/logic/Current";
import { usePagination } from "@/modules/usePagination";
import { useTypedLazyQuery } from "@/services/graphApi";
import {
	$,
	CampaignConsistencyCheckOrderByInput,
	ValueTypes,
	ZeusSelect,
} from "@/services/graphApi/graphql-zeus";
import { useEffect, useState } from "react";

export type CampaignConsistencyCheckQueryArgument =
	| "gameId_contains"
	| "platform"
	| "date_gte"
	| "date_lte"
	| "campaignId_contains";

export const useCampaignConsistencyChecksData = () => {
	const { currentOrganization } = useCurrentOrganization();
	const pagination = usePagination();
	const [filters, setFilters] = useState<
		FilterState<CampaignConsistencyCheckQueryArgument, never>
	>({});

	const [
		getCampaignConsistencyChecks,
		state,
	] = useCampaignConsistencyChecksQuery();

	useEffect(() => {
		pagination.setCount(
			state.data?.campaignConsistencyChecksConnection.aggregate.count || 0
		);
		pagination.setLoading(state.loading);
		pagination.setError(state.error && JSON.stringify(state.error));
		pagination.setPageInfo(
			state.data?.campaignConsistencyChecksConnection.pageInfo
		);
	}, [pagination, state.data, state.error, state.loading]);

	const { reset } = pagination;
	useEffect(() => {
		reset();
	}, [filters, currentOrganization, reset]);

	useEffect(() => {
		getCampaignConsistencyChecks({
			variables: {
				...pagination.pagination,
				...filters,
				organizationId: currentOrganization?.id,
			},
		});
	}, [
		currentOrganization,
		pagination.pagination,
		filters,
		getCampaignConsistencyChecks,
	]);

	return {
		state,
		pagination,
		filters,
		setFilters,
	};
};

const useCampaignConsistencyChecksQuery = () =>
	useTypedLazyQuery(
		{
			campaignConsistencyChecksConnection: [
				{
					first: $`first`,
					last: $`last`,
					before: $`before`,
					after: $`after`,
					skip: $`skip`,
					orderBy:
						CampaignConsistencyCheckOrderByInput.createdAt_DESC,
					where: {
						platform: $`platform`,
						campaignId_contains: $`campaignId_contains`,
						gameId_contains: $`gameId_contains`,
						date_gte: $`date_gte`,
						date_lte: $`date_lte`,
						organizationAttributes_some: {
							organization: { id: $`organizationId` },
						},
					},
				},
				CampaignConsistencyCheckConnection(),
			],
		},
		{
			notifyOnNetworkStatusChange: true,
		}
	);

const CampaignConsistencyCheckConnection = () =>
	ZeusSelect<ValueTypes["CampaignConsistencyCheckConnection"]>()({
		aggregate: {
			count: true,
		},
		edges: {
			node: CampaignConsistencyCheckSelector(),
			cursor: true,
		},
		pageInfo: {
			hasNextPage: true,
			hasPreviousPage: true,
			startCursor: true,
			endCursor: true,
		},
	});

const CampaignConsistencyCheckSelector = () =>
	ZeusSelect<ValueTypes["CampaignConsistencyCheck"]>()({
		id: true,
		createdAt: true,
		updatedAt: true,
		date: true,
		gameId: true,
		campaignId: true,
		platform: true,
		values: [
			{},
			{
				source: true,
				spend: true,
				iapGrossRevenue: true,
				purchases: true,
				payers: true,
				impressions: true,
				clicks: true,
				installs: true,
			},
		],
	});
